// src/components/Pricing/PricingCard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Check, Clock, CheckCircle, Zap, ArrowRight, Shield, AlertCircle, XCircle } from 'lucide-react';
import {
  createTrialSubscription,
  createUpgradeSubscription,
  redirectToCheckout,
  PRICING_PLANS,
  getSubscriptionStatus,
  checkTrialEligibility
} from '../../services/stripeService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';

const PricingCard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isLaunchOffer] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isTrialEligible, setIsTrialEligible] = useState(true);

  // Debug: Log the price IDs
  useEffect(() => {
    console.log('PRICING_PLANS:', PRICING_PLANS);
    console.log('Environment variables check:', {
      monthlyPriceId: process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID,
      yearlyPriceId: process.env.REACT_APP_STRIPE_YEARLY_PRICE_ID,
      launchMonthlyPriceId: process.env.REACT_APP_STRIPE_LAUNCH_MONTHLY_PRICE_ID,
      launchYearlyPriceId: process.env.REACT_APP_STRIPE_LAUNCH_YEARLY_PRICE_ID,
      publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? 'Set' : 'Not set'
    });
  }, []);

  const loadUserStatus = useCallback(async () => {
    if (!user) return;
    
    const [status, eligible] = await Promise.all([
      getSubscriptionStatus(user.id),
      checkTrialEligibility(user.id)
    ]);
    
    setSubscriptionStatus(status);
    setIsTrialEligible(eligible);
  }, [user]);

  useEffect(() => {
    if (user) {
      loadUserStatus();
    }
  }, [user, loadUserStatus]);

  useEffect(() => {
    // Check if we're returning from a checkout
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('checkout') === 'complete' || urlParams.get('upgrade') === 'success') {
      // Force reload subscription status
      loadUserStatus();
    }
  }, [loadUserStatus]);

  const handleStartTrial = async (planType) => {
    setLoading(true);
    
    try {
      // If user is not logged in, redirect to registration
      if (!user) {
        localStorage.setItem('selectedPlan', JSON.stringify({
          planType: planType,
          isLaunchOffer: isLaunchOffer
        }));
        window.location.href = '/register';
        return;
      }

      // Check if user is already in trial and trying to upgrade
      if (subscriptionStatus?.subscription_status === 'trial') {
        // Create a subscription update/upgrade session instead of a new trial
        const plan = PRICING_PLANS[planType];
        const priceId = isLaunchOffer ? plan.launchPriceId : plan.priceId;
        
        try {
          // Create upgrade session (not trial)
          const response = await createUpgradeSubscription(
            priceId,
            user.id,
            user.email
          );
          
          // Check if we got a success response without sessionId (subscription was updated directly)
          if (response.success && !response.sessionId) {
            // Subscription was updated directly, refresh the page
            window.location.href = '/dashboard?upgrade=success';
            return;
          }
          
          // Otherwise, redirect to Stripe checkout
          if (response.sessionId) {
            await redirectToCheckout(response.sessionId);
          } else {
            throw new Error('No session ID returned from server');
          }
        } catch (upgradeError) {
          console.error('Upgrade error details:', upgradeError);
          
          // Show more specific error message
          if (upgradeError.message.includes('not configured')) {
            alert('The payment system is not properly configured. Please contact support.');
          } else if (upgradeError.message.includes('profile')) {
            alert('There was an issue with your account profile. Please try logging out and back in.');
          } else {
            alert(`Error: ${upgradeError.message || 'Failed to process upgrade. Please try again.'}`);
          }
          return;
        }
      }

      // For canceled users or new trials - create appropriate subscription
      const plan = PRICING_PLANS[planType];
      const priceId = isLaunchOffer ? plan.launchPriceId : plan.priceId;
      
      // If user has used trial (including canceled users), create a regular subscription, not a trial
      if (!isTrialEligible || subscriptionStatus?.subscription_status === 'canceled') {
        const response = await createUpgradeSubscription(
          priceId,
          user.id,
          user.email
        );
        
        if (response.sessionId) {
          await redirectToCheckout(response.sessionId);
        } else {
          throw new Error('Failed to create subscription');
        }
      } else {
        // New user - create trial
        const { sessionId } = await createTrialSubscription(
          priceId,
          user.id,
          user.email
        );
        
        await redirectToCheckout(sessionId);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert(`Error: ${error.message || 'There was an error processing your request. Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = PRICING_PLANS[billingCycle];
  const displayPrice = isLaunchOffer ? currentPlan.launchPrice : currentPlan.amount;
  const savings = billingCycle === 'yearly' ? 118 : 0;

  // Check user's subscription status
  const isCanceled = subscriptionStatus?.subscription_status === 'canceled';
  const hasActiveSubscription = subscriptionStatus?.subscription_status === 'active';
  const isInTrial = subscriptionStatus?.subscription_status === 'trial' && !subscriptionStatus?.isTrialExpired;
  const isPastDue = subscriptionStatus?.subscription_status === 'past_due';
  const isExpired = subscriptionStatus?.subscription_status === 'expired' || 
                    (subscriptionStatus?.subscription_status === 'trial' && subscriptionStatus?.isTrialExpired);

  // If user's trial has expired or payment failed
  if (isExpired || isPastDue) {
    return (
      <div className="relative max-w-4xl mx-auto">
        <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden max-w-md mx-auto">
          {/* Gradient border effect - Using inline style */}
          <div 
          className="absolute inset-0"
          style={{ 
            backgroundImage: 'linear-gradient(to bottom right, rgba(239, 68, 68, 0.1), rgba(185, 28, 28, 0.1))' 
          }}
        ></div>
        
        {/* Content Container */}
        <div className="relative z-10">
          {/* Header with gradient */}
          <div 
            className="p-8 text-center text-white"
            style={{ 
              backgroundImage: 'linear-gradient(to right, #dc2626, #b91c1c)' 
            }}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <AlertCircle className="w-4 h-4 mr-2" />
              {isPastDue ? 'Payment Required' : 'Trial Ended'}
            </div>
            <h3 className="text-2xl font-bold mb-2">
              {isPastDue 
                ? 'Payment Failed' 
                : 'Your Free Trial Has Ended'}
            </h3>
            <p className="text-white/90">
              {isPastDue
                ? 'Update your payment method to continue'
                : 'Subscribe to continue using Profit Clarity'}
            </p>
          </div>
          
          <div className="p-8">
            {/* Status Message */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                {isPastDue ? (
                  <>
                    <strong>Action Required:</strong> Your payment method was declined. 
                    Please update your card to restore access.
                  </>
                ) : (
                  <>
                    <strong>Access Limited:</strong> Your 14-day trial ended. 
                    Subscribe now to continue analyzing your profitability.
                  </>
                )}
              </p>
            </div>
            
            {/* What you're missing section */}
            <div 
              className="rounded-lg p-4 mb-6"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgba(189, 180, 226, 0.1), rgba(44, 54, 94, 0.1))' 
              }}
            >
              <p className="text-sm font-medium text-gray-700 mb-3">What you're missing:</p>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center">
                  <XCircle size={14} className="text-red-500 mr-2" />
                  <span>Profit analysis dashboard</span>
                </div>
                <div className="flex items-center">
                  <XCircle size={14} className="text-red-500 mr-2" />
                  <span>Cost discovery tools</span>
                </div>
                <div className="flex items-center">
                  <XCircle size={14} className="text-red-500 mr-2" />
                  <span>Recommendations</span>
                </div>
                <div className="flex items-center">
                  <XCircle size={14} className="text-red-500 mr-2" />
                  <span>PDF report generation</span>
                </div>
              </div>
            </div>
            
            {/* CTA Button */}
            {isPastDue ? (
              <button
                onClick={() => {/* Handle update payment */}}
                className="w-full text-white py-3 px-6 rounded-xl font-semibold hover:shadow-xl transform hover:scale-[1.02] transition-all mb-3"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, #2c365e, #484d6d)' 
                }}
              >
                Update Payment Method
                <ArrowRight className="inline-block ml-2" size={20} />
              </button>
            ) : (
              <button
                onClick={() => handleStartTrial(billingCycle)}
                disabled={loading}
                className="w-full text-white py-3 px-6 rounded-xl font-semibold hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, #2c365e, #484d6d)' 
                }}
              >
                {loading ? 'Processing...' : 'Subscribe Now'}
                <ArrowRight className="inline-block ml-2" size={20} />
              </button>
            )}
            
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-white text-gray-600 py-2 px-4 rounded-xl font-medium border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
            >
              Return to Homepage
            </button>
            
            {/* Support Link */}
            <p className="mt-6 text-center text-xs text-gray-500">
              Need help? Contact{' '}
              <a href="mailto:support@getprofitclarity.com" className="text-sws-purple hover:text-sws-navy">
                support@getprofitclarity.com
              </a>
            </p>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // If user is already in trial
  if (isInTrial) {
    const daysRemaining = subscriptionStatus?.daysRemaining || 0;
    const trialEndDate = subscriptionStatus?.trial_end_date ? 
      new Date(subscriptionStatus.trial_end_date).toLocaleDateString() : 'N/A';
    
    const handleCancelTrial = async () => {
      if (window.confirm('Are you sure you want to cancel your trial? You will lose access immediately.')) {
        setLoading(true);
        try {
          const { error } = await supabase.functions.invoke('cancel-subscription', {
            body: { userId: user.id, email: user.email }
          });
          
          if (error) throw error;
          
          await supabase
            .from('business_profiles')
            .update({ 
              subscription_status: 'cancelled',
              subscription_end_date: new Date().toISOString()
            })
            .eq('user_id', user.id);
          
          alert('Your trial has been cancelled successfully.');
          window.location.href = '/';
        } catch (error) {
          console.error('Error cancelling trial:', error);
          alert('Unable to cancel at this time. Please contact support.');
        } finally {
          setLoading(false);
        }
      }
    };
    
    return (
      <div className="relative max-w-4xl mx-auto">
        <div className="max-w-md mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 p-8 border-b border-green-200">
            <div className="text-center">
              <div className="inline-flex items-center bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <CheckCircle className="w-4 h-4 mr-2" />
                Trial Active
              </div>
              <h3 className="text-2xl font-bold text-sws-navy mb-2">
                You're in Your Free Trial!
              </h3>
            <p className="text-lg text-sws-slate">
              {daysRemaining} days remaining until {trialEndDate}
            </p>
          </div>
        </div>
        
        <div className="p-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>What happens next:</strong> Your subscription will 
              automatically continue at your selected plan rate after the trial ends.
            </p>
          </div>
          
          <div className="space-y-2 text-sm text-sws-slate mb-6">
            <p className="flex items-center">
              <CheckCircle size={14} className="text-green-600 mr-2" />
              Full access to all features
            </p>
            <p className="flex items-center">
              <AlertCircle size={14} className="text-blue-600 mr-2" />
              Add payment method before trial ends to continue
            </p>
          </div>
          
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="w-full text-white py-3 px-6 rounded-xl font-semibold hover:shadow-xl transform hover:scale-[1.02] transition-all mb-3"
            style={{ 
              backgroundImage: 'linear-gradient(to right, #2c365e, #484d6d)' 
            }}
          >
            Return to Dashboard
          </button>
          
          <button
            onClick={handleCancelTrial}
            disabled={loading}
            className="w-full bg-white text-gray-600 py-2 px-4 rounded-xl font-medium border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
          >
            {loading ? 'Cancelling...' : 'Cancel Trial'}
          </button>
        </div>
        </div>
      </div>
    );
  }

  // If user has active subscription
  if (hasActiveSubscription) {
    return (
      <div className="relative max-w-4xl mx-auto">
        <div className="max-w-md mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 p-8 border-b border-green-200">
            <div className="text-center">
              <div className="inline-flex items-center bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <CheckCircle className="w-4 h-4 mr-2" />
                Active Subscription
              </div>
              <h3 className="text-2xl font-bold text-sws-navy mb-2">
                You're All Set!
              </h3>
            <p className="text-lg text-sws-slate">
              Your subscription is active
            </p>
          </div>
        </div>
        
        <div className="p-8">
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="w-full text-white py-3 px-6 rounded-xl font-semibold hover:shadow-xl transform hover:scale-[1.02] transition-all"
            style={{ 
              backgroundImage: 'linear-gradient(to right, #2c365e, #484d6d)' 
            }}
          >
            Go to Dashboard
          </button>
        </div>
        </div>
      </div>
    );
  }

  const features = [
    'Analysis of up to 8 key offerings',
    'Comprehensive profitability dashboard',
    'Time cost & overhead allocation tools',
    'Strategic recommendations',
    'Unlimited PDF reports & exports',
    'Regular feature updates',
    'Email support'
  ];

  return (
    <div id="pricing-section" className="relative max-w-4xl mx-auto">
      {/* Card Container with gradient border effect */}
      <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Gradient border effect - Using inline style to ensure it works in production */}
        <div 
          className="absolute inset-0"
          style={{ 
            backgroundImage: 'linear-gradient(to bottom right, rgba(189, 180, 226, 0.1), rgba(44, 54, 94, 0.1), rgba(189, 180, 226, 0.1))' 
          }}
        ></div>
        
        {/* Content Container */}
        <div className="relative z-10 p-8 lg:p-10">
          {/* Header with Launch Badge */}
          <div className="text-center mb-8">
            {isLaunchOffer && (
              <div 
                className="inline-flex items-center border border-green-200 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, rgb(240, 253, 244), rgb(236, 253, 245))' 
                }}
              >
                <Zap className="w-4 h-4 mr-2" />
                Limited Time Launch Pricing
              </div>
            )}
            <h3 className="text-2xl font-bold text-sws-navy mb-2">
              Profit Clarity
            </h3>
            <p className="text-sws-slate">
              Transform your business with data-driven insights
            </p>
          </div>

          {/* Trial Benefits */}
          <div 
            className="rounded-xl p-4 mb-6"
            style={{ 
              backgroundImage: 'linear-gradient(to right, rgba(189, 180, 226, 0.1), rgba(44, 54, 94, 0.1))' 
            }}
          >
            <h4 className="font-semibold text-sws-navy mb-2 flex items-center">
              <Clock size={16} className="mr-2" />
              Your 14-Day Trial Includes:
            </h4>
            <ul className="text-sm text-sws-slate space-y-1">
              <li>• Full access to all features</li>
              <li>• Analyze up to 8 offers</li>
              <li>• Generate unlimited reports</li>
              <li>• Cancel anytime before trial ends</li>
            </ul>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 p-1 rounded-xl inline-flex">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-sws-navy shadow-md'
                    : 'text-gray-600 hover:text-sws-navy'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all ${
                  billingCycle === 'yearly'
                    ? 'bg-white text-sws-navy shadow-md'
                    : 'text-gray-600 hover:text-sws-navy'
                }`}
              >
                Yearly
                {billingCycle === 'yearly' && (
                  <span className="ml-2 text-green-600 text-xs">Save ${savings}</span>
                )}
              </button>
            </div>
          </div>

          {/* Pricing Display */}
          <div className="text-center mb-8">
            <p className="text-sws-slate text-sm mb-2">After trial ends:</p>
            <div className="mb-4">
              <span className="text-5xl font-bold text-sws-navy">${displayPrice}</span>
              <span className="text-sws-slate ml-2">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
            </div>
            
            {isLaunchOffer && (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-gray-400 line-through text-lg">${currentPlan.amount}</span>
                  <span className="bg-green-100 text-green-700 text-sm font-semibold px-3 py-1 rounded-full">
                    Save ${currentPlan.amount - displayPrice}
                    {billingCycle === 'yearly' ? '/year' : '/month'}
                  </span>
                </div>
                <p className="text-green-600 font-medium text-sm">
                  Launch pricing - Limited time!
                </p>
              </div>
            )}

            {billingCycle === 'yearly' && (
              <div 
                className="mt-4 rounded-lg p-3"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, rgba(189, 180, 226, 0.1), rgba(44, 54, 94, 0.1))' 
                }}
              >
                <p className="text-sws-navy text-sm font-medium">
                  {isLaunchOffer ? (
                    <>
                      Total annual savings: <span className="text-green-600 font-bold">${(currentPlan.amount - displayPrice) + 118}</span>
                      <span className="text-xs block text-sws-slate mt-1">
                        (${currentPlan.amount - displayPrice} launch discount + $118 annual plan savings)
                      </span>
                    </>
                  ) : (
                    <>Save <span className="font-bold">${savings}</span> with annual billing</>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Features List */}
          <div className="space-y-4 mb-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                  <Check size={14} className="text-green-600" />
                </div>
                <span className="ml-3 text-sws-slate">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA Button - Dynamic based on status */}
          {isCanceled ? (
            // User canceled - show reactivate/subscribe button
            <button
              onClick={() => handleStartTrial(billingCycle)}
              disabled={loading}
              className="w-full text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group"
              style={{ 
                backgroundImage: 'linear-gradient(to right, #2c365e, #484d6d)' 
              }}
            >
              {loading ? 'Processing...' : 'Reactivate Subscription'}
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </button>
          ) : !isTrialEligible ? (
            // Trial already used - show subscribe button
            <button
              onClick={() => handleStartTrial(billingCycle)}
              disabled={loading}
              className="w-full text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group"
              style={{ 
                backgroundImage: 'linear-gradient(to right, #2c365e, #484d6d)' 
              }}
            >
              {loading ? 'Processing...' : 'Subscribe Now'}
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </button>
          ) : (
            // New user - can start trial
            <button
              onClick={() => handleStartTrial(billingCycle)}
              disabled={loading}
              className="w-full text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group"
              style={{ 
                backgroundImage: 'linear-gradient(to right, #2c365e, #484d6d)' 
              }}
            >
              {loading ? 'Processing...' : 'Start Your 14-Day Free Trial'}
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </button>
          )}

          {/* Show appropriate message */}
          {!isTrialEligible && !isCanceled && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-center text-orange-700 text-sm font-medium">
                Trial already used. Subscribe to continue access.
              </p>
            </div>
          )}

          {/* Trial Terms */}
          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-center text-sm text-sws-slate">
              <Shield className="w-4 h-4 mr-2 text-green-600" />
              <span>
                {isCanceled || !isTrialEligible 
                  ? 'Secure payment processing' 
                  : 'No credit card required to start'}
              </span>
            </div>
            <p className="text-center text-xs text-gray-500">
              {isCanceled || !isTrialEligible
                ? `$${displayPrice}/${billingCycle === 'monthly' ? 'month' : 'year'}. Cancel anytime.`
                : `Free for 14 days. Add payment before trial ends to continue at $${displayPrice}/${billingCycle === 'monthly' ? 'month' : 'year'}.`}
            </p>
          </div>

          {/* Launch Offer Deadline */}
          {isLaunchOffer && (
            <div 
              className="mt-4 p-3 rounded-lg text-center"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgba(189, 180, 226, 0.1), rgba(44, 54, 94, 0.1))' 
              }}
            >
              <p className="text-sws-navy text-sm font-medium">
                <span role="img" aria-label="celebration">🎉</span> Launch pricing locked in after trial
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingCard;